import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import axios, { AxiosError, AxiosResponse } from "axios";
import { buildAdminResourcePathForCurrentUser } from "../ResourceBuilder";
import type { RootState } from "../store";

export type LabcorpConfiguration = {
    tokenApiEndpoint: string;
    orderApiEndpoint: string;
    labcorpId: string;
    sendingFacility: string;
    sendingApplication: string;
    receivingFacility: string;
    receivingApplication: string;
    clientId: string;
    clientSecret: string;
    accudrawSoapEndpoint: string;
    accudrawUserName: string;
    accudrawPassword: string;
};

export type LabcorpSettings = {
    id: number;
    lastUpdatedOn: string;
    lastUpdatedBy: number;
    config: Partial<LabcorpConfiguration>;
};

export type LabcorpSettingsMutation = {
    message: string;
    vendor: LabcorpSettings;
};

export type UpdateLabcorpSettings = Pick<LabcorpSettings, "id" | "config">;
export type CreateLabcorpSettings = Pick<LabcorpSettings, "config">;

const tagTypes = ["configuration"];

const labcorpConfigurationApi = createApi({
    reducerPath: "lobbie/admin/labcorp/configuration",
    baseQuery: fetchBaseQuery({}),
    tagTypes,
    endpoints: (builder) => ({
        getLabcorpConfiguration: builder.query<LabcorpSettings | undefined, void>({
            queryFn: async (_, api) => {
                // this is Admin Only... for now.
                const getState = api.getState as () => RootState;
                const url = buildAdminResourcePathForCurrentUser(
                    getState(),
                    "labs/settings/vendors/labcorp",
                );
                try {
                    const response = await axios.get<LabcorpSettings>(url!, {
                        withCredentials: true,
                    });
                    return { data: response.data };
                } catch (error) {
                    const axiosError = error as AxiosError;
                    return {
                        data: undefined,
                        error: {
                            status: axiosError.status ?? 0,
                            data: axiosError.response?.data,
                        },
                    };
                }
            },
            providesTags: (result) =>
                result
                    ? [
                          { type: "configuration", id: result.id },
                          { type: "configuration", id: "LIST" },
                      ]
                    : [{ type: "configuration", id: "LIST" }],
        }),
        createLabcorpConfiguration: builder.mutation<
            LabcorpSettingsMutation,
            CreateLabcorpSettings
        >({
            queryFn: async (payload, api) => {
                // this is Admin Only... for now.
                const getState = api.getState as () => RootState;
                const url = buildAdminResourcePathForCurrentUser(
                    getState(),
                    "labs/settings/vendors/labcorp",
                );
                try {
                    const response = await axios.post<
                        LabcorpSettingsMutation,
                        AxiosResponse<LabcorpSettingsMutation>,
                        CreateLabcorpSettings
                    >(url!, payload, { withCredentials: true });
                    return { data: response.data };
                } catch (error) {
                    const axiosError = error as AxiosError;
                    return {
                        data: undefined,
                        error: {
                            status: axiosError.status ?? 0,
                            data: axiosError.response?.data,
                        },
                    };
                }
            },
            invalidatesTags: [{ type: "configuration", id: "LIST" }],
        }),
        updateLabcorpConfiguration: builder.mutation<
            LabcorpSettingsMutation,
            UpdateLabcorpSettings
        >({
            queryFn: async (payload, api) => {
                // this is Admin Only... for now.
                const getState = api.getState as () => RootState;
                const url = buildAdminResourcePathForCurrentUser(
                    getState(),
                    "labs/settings/vendors/labcorp",
                );
                try {
                    const response = await axios.put<
                        LabcorpSettingsMutation,
                        AxiosResponse<LabcorpSettingsMutation>,
                        CreateLabcorpSettings
                    >(url!, payload, { withCredentials: true });
                    return { data: response.data };
                } catch (error) {
                    const axiosError = error as AxiosError;
                    return {
                        data: undefined,
                        error: {
                            status: axiosError.status ?? 0,
                            data: axiosError.response?.data,
                        },
                    };
                }
            },
            invalidatesTags: (result) => [{ type: "configuration", id: result?.vendor.id }],
        }),
    }),
});

export const {
    useGetLabcorpConfigurationQuery,
    useCreateLabcorpConfigurationMutation,
    useUpdateLabcorpConfigurationMutation,
} = labcorpConfigurationApi;

export default labcorpConfigurationApi;
